// First-person HUD, shown only while in play mode: crosshair, health bar, points, current weapon +
// ammo, a shop buy-prompt, a transient message toast, and a control hint. Purely presentational
// (pointer-events: none). Extended from the movement-only HUD when weapons + the shop landed.

export class PlayerHud {
  private root: HTMLDivElement;
  private fg: HTMLDivElement;
  private points: HTMLDivElement;
  private weapon: HTMLDivElement;
  private prompt: HTMLDivElement;
  private message: HTMLDivElement;
  // change-detection caches: these setters are called every frame; skip the DOM write when unchanged.
  private lastF = -1;             // clamped health frac (out-of-range sentinel → first call always writes)
  private lastPoints = NaN;
  private lastPrompt: string | null | undefined;
  private lastMessage: string | undefined;
  private wName: string | null = null; private wMag = -1; private wReserve = NaN; private wReloading = false;

  constructor(container: HTMLElement) {
    const root = document.createElement("div");
    Object.assign(root.style, {
      position: "fixed", left: "0", top: "0", width: "100%", height: "100%",
      pointerEvents: "none", display: "none", zIndex: "15",
      font: "12px/1.4 'Courier New', monospace", color: "#dfe3ea",
    } as CSSStyleDeclaration);

    // crosshair dot
    const cross = document.createElement("div");
    Object.assign(cross.style, {
      position: "absolute", left: "50%", top: "50%", width: "6px", height: "6px",
      marginLeft: "-3px", marginTop: "-3px", borderRadius: "50%",
      background: "rgba(255,255,255,0.85)", boxShadow: "0 0 0 1px rgba(0,0,0,0.6)",
    } as CSSStyleDeclaration);
    root.appendChild(cross);

    // points (top-center)
    this.points = document.createElement("div");
    Object.assign(this.points.style, {
      position: "absolute", left: "50%", top: "14px", transform: "translateX(-50%)",
      padding: "4px 12px", background: "rgba(12,16,20,0.8)", border: "1px solid #2a3038",
      color: "#ffd23f", fontSize: "16px", fontWeight: "bold", whiteSpace: "nowrap",
    } as CSSStyleDeclaration);
    this.points.textContent = "▲ 0";
    root.appendChild(this.points);

    // message toast (upper-middle)
    this.message = document.createElement("div");
    Object.assign(this.message.style, {
      position: "absolute", left: "50%", top: "34%", transform: "translateX(-50%)",
      padding: "6px 14px", background: "rgba(12,16,20,0.85)", border: "1px solid #2a3038",
      color: "#9fe1cb", fontSize: "14px", whiteSpace: "nowrap", opacity: "0",
      transition: "opacity 0.2s ease",
    } as CSSStyleDeclaration);
    root.appendChild(this.message);

    // buy prompt (below crosshair)
    this.prompt = document.createElement("div");
    Object.assign(this.prompt.style, {
      position: "absolute", left: "50%", top: "60%", transform: "translateX(-50%)",
      padding: "6px 14px", background: "rgba(12,16,20,0.8)", border: "1px solid #3a78ff",
      color: "#dfe3ea", fontSize: "13px", whiteSpace: "nowrap", display: "none",
    } as CSSStyleDeclaration);
    root.appendChild(this.prompt);

    // weapon + ammo (bottom-center, above the health bar)
    this.weapon = document.createElement("div");
    Object.assign(this.weapon.style, {
      position: "absolute", left: "50%", bottom: "62px", transform: "translateX(-50%)",
      color: "#dfe3ea", fontSize: "15px", whiteSpace: "nowrap", textAlign: "center",
    } as CSSStyleDeclaration);
    root.appendChild(this.weapon);

    // health bar (bottom-center)
    const barWrap = document.createElement("div");
    Object.assign(barWrap.style, {
      position: "absolute", left: "50%", bottom: "44px", transform: "translateX(-50%)",
      width: "220px", height: "10px", background: "rgba(12,16,20,0.8)", border: "1px solid #2a3038",
    } as CSSStyleDeclaration);
    const fg = document.createElement("div");
    Object.assign(fg.style, { height: "100%", width: "100%", background: "hsl(120,60%,45%)" } as CSSStyleDeclaration);
    barWrap.appendChild(fg);
    root.appendChild(barWrap);

    // control hint
    const hint = document.createElement("div");
    Object.assign(hint.style, {
      position: "absolute", left: "50%", bottom: "22px", transform: "translateX(-50%)",
      color: "#8b93a0", whiteSpace: "nowrap",
    } as CSSStyleDeclaration);
    hint.textContent = "click: fire · R: reload · 1-9: weapon · E: buy · Shift: sprint · Space: jump · Esc: exit";
    root.appendChild(hint);

    container.appendChild(root);
    this.root = root;
    this.fg = fg;
  }

  show(): void {
    this.root.style.display = "block";
  }
  hide(): void {
    this.root.style.display = "none";
  }
  setHealth(frac: number): void {
    const f = Math.max(0, Math.min(1, frac));
    if (f === this.lastF) return;
    this.lastF = f;
    this.fg.style.width = f * 100 + "%";
    this.fg.style.background = `hsl(${Math.round(f * 120)},60%,45%)`;
  }
  setPoints(n: number): void {
    const r = Math.round(n);
    if (r === this.lastPoints) return;
    this.lastPoints = r;
    this.points.textContent = "▲ " + r;
  }
  setWeapon(name: string, mag: number, reserve: number, reloading: boolean): void {
    if (name === this.wName && mag === this.wMag && reserve === this.wReserve && reloading === this.wReloading) return;
    this.wName = name; this.wMag = mag; this.wReserve = reserve; this.wReloading = reloading;
    const res = Number.isFinite(reserve) ? String(reserve) : "∞";
    const ammo = reloading ? "RELOADING…" : `${mag} / ${res}`;
    this.weapon.innerHTML = `${escapeHtml(name)}<br><span style="color:#b8c0cc">${ammo}</span>`;
  }
  setPrompt(text: string | null): void {
    if (text === this.lastPrompt) return;
    this.lastPrompt = text;
    if (text) {
      this.prompt.textContent = text;
      this.prompt.style.display = "block";
    } else {
      this.prompt.style.display = "none";
    }
  }
  setMessage(text: string): void {
    if (text === this.lastMessage) return;
    this.lastMessage = text;
    if (text) {
      this.message.textContent = text;
      this.message.style.opacity = "1";
    } else {
      this.message.style.opacity = "0";
    }
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string);
}
